import { useEffect, useState } from 'react';
import { Car, Info } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Modal } from '@/components/Shared/Modal';
import { Button } from '@/components/Shared/Button';
import { Input } from '@/components/Shared/Input';
import type { EndTripDialogState } from '@/hooks/useTripDialogs';
import { useMyVehicleTripsStore } from '@/store/useMyVehicleTripsStore';
import { formatKm } from '@/utils/distance';
import { formatDateTime, isoDate, localTimeToUtcIso, parseUtcTimestamp } from '@/utils/dateUtils';
import { DESTINATION_MAX_LENGTH, PURPOSE_MAX_LENGTH } from '@/utils/logbookLimits';
import { BUSINESS, DRIVER, PRIVATE, canConfirmTripEnd, needsArrivalTime, parseKm, prefillTripText } from '@/utils/vehicleTrips';

interface EndTripDialogProps extends EndTripDialogState {
  busy?: boolean;
}

const FIELD = 'h-11 text-[15px]';
const SINCE: Intl.DateTimeFormatOptions = { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };

/**
 * Ends the own trip (FAFD-68). The start can still be corrected here (prefilled). Start, destination
 * and — for a business trip — purpose are required as the tenant set it (official logbook); the
 * reading is optional, without it the trip is "nicht abgestellt" and the driver parks the vehicle later. A trip from an earlier day also needs its arrival. Mode
 * `clockOut` comes with the clock-out: "Ausstempeln und Fahrt beenden" or "Nur ausstempeln,
 * Fahrt läuft weiter". A passenger only confirms the end of the ride. Driven by useTripDialogs.
 */
export function EndTripDialog({ isOpen, mode, trip, busy = false, onConfirm, onCancel }: EndTripDialogProps) {
  const { t } = useTranslation('vehicles');
  const rules = useMyVehicleTripsStore((s) => s.endRules);
  const [odometer, setOdometer] = useState('');
  const [startLocation, setStartLocation] = useState('');
  const [destination, setDestination] = useState('');
  const [purpose, setPurpose] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');

  useEffect(() => {
    if (!isOpen || !trip) return;
    setOdometer('');
    // Empty or still "laut Dienstplan": the suggestion for the departure (FAFD-68).
    const plan = trip.suggestion;
    setStartLocation(prefillTripText(trip.startLocation, plan?.startLocation, plan?.placeholder));
    setDestination(prefillTripText(trip.destination, plan?.destination, plan?.placeholder));
    setPurpose(prefillTripText(trip.purpose, plan?.purpose, plan?.placeholder));
    const started = parseUtcTimestamp(trip.startedAt);
    setArrivalDate(started ? isoDate(started) : isoDate(new Date()));
    setArrivalTime('');
  }, [isOpen, trip]);

  if (!trip) return null;

  const isDriver = trip.role === DRIVER;
  const isPrivate = trip.purposeKind === PRIVATE;
  const needsArrival = isDriver && needsArrivalTime(trip.startedAt, new Date());
  const km = parseKm(odometer);
  const startKm = trip.startOdometerKm ?? null;
  const canConfirm = canConfirmTripEnd({
    isDriver,
    odometer,
    startLocation,
    destination,
    purpose,
    purposeKind: trip.purposeKind ?? BUSINESS,
    needsArrival,
    arrivalTime: arrivalDate ? arrivalTime : '',
    rules,
  });
  const req = (label: string, required: boolean) => (required ? `${label} *` : label);

  const confirm = () => {
    if (!canConfirm) return;
    onConfirm({
      endTrip: isDriver
        ? {
            odometerKm: km,
            // An emptied field goes as "" and clears the prefill; null would keep it (FAFD-68).
            startLocation: startLocation.trim(),
            destination: destination.trim(),
            purpose: isPrivate ? null : purpose.trim(),
            endedAt: needsArrival ? localTimeToUtcIso(arrivalDate, arrivalTime) : null,
          }
        : {},
    });
  };

  const titleKey = mode === 'clockOut' ? 'trip.endTitle.clockOut' : isDriver ? 'trip.endTitle.end' : 'trip.endTitle.ride';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={t(titleKey)}
      size="sm"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('clock.cancel')}
          </Button>
          {mode === 'clockOut' && (
            <Button variant="secondary" onClick={() => onConfirm({ endTrip: null })} disabled={busy}>
              {t('trip.clockOutOnly')}
            </Button>
          )}
          <Button onClick={confirm} disabled={busy || !canConfirm}>
            {t(mode === 'clockOut' ? 'trip.clockOutAndEnd' : isDriver ? 'trip.end' : 'trip.endRide')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="flex gap-2 text-[13px] text-slate-700 tabular-nums">
          <Car className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" strokeWidth={2} aria-hidden />
          <span>
            <span className="font-mono">{trip.licensePlate}</span>
            {' · '}
            {t('trip.since', { time: formatDateTime(parseUtcTimestamp(trip.startedAt), SINCE) })}
            {startKm !== null && ` · ${t('park.startKm', { km: formatKm(startKm) })}`}
          </span>
        </p>

        {mode === 'clockOut' && <p className="text-[12px] text-slate-500">{t('trip.clockOutHint')}</p>}

        {isDriver && (
          <>
            <div>
              <Input
                label={t('clock.odometerLabel')}
                inputMode="numeric"
                autoComplete="off"
                value={odometer}
                onChange={(e) => setOdometer(e.target.value.replace(/\D/g, ''))}
                placeholder={t('clock.odometerPlaceholder')}
                className={`${FIELD} font-mono tabular-nums`}
              />
              <span className="mt-1 flex gap-1.5 text-[12px] text-slate-500">
                <Info className="w-3.5 h-3.5 shrink-0 mt-px" strokeWidth={2} aria-hidden />
                {odometer === ''
                  ? t('trip.odometerOptional')
                  : km !== null && startKm !== null && km >= startKm
                    ? t('clock.drivenKm', { km: formatKm(km - startKm) })
                    : km !== null && startKm !== null
                      ? t('park.belowStart', { km: formatKm(startKm) })
                      : null}
              </span>
            </div>
            <Input
              label={req(t('trip.startLocationLabel'), rules.startRequired)}
              value={startLocation}
              maxLength={DESTINATION_MAX_LENGTH}
              onChange={(e) => setStartLocation(e.target.value)}
              placeholder={t('trip.startLocationPlaceholder')}
              className={FIELD}
            />
            <Input
              label={req(t('trip.destinationLabel'), rules.destinationRequired)}
              value={destination}
              maxLength={DESTINATION_MAX_LENGTH}
              onChange={(e) => setDestination(e.target.value)}
              placeholder={t('clock.destinationPlaceholder')}
              className={FIELD}
            />
            {!isPrivate && (
              <Input
                label={req(t('trip.purposeLabel'), rules.purposeRequired)}
                value={purpose}
                maxLength={PURPOSE_MAX_LENGTH}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder={t('clock.purposePlaceholder')}
                className={FIELD}
              />
            )}
            {needsArrival && (
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label={t('trip.arrivalDate')}
                    type="date"
                    value={arrivalDate}
                    onChange={(e) => setArrivalDate(e.target.value)}
                    className={FIELD}
                  />
                  <Input
                    label={`${t('trip.arrivalTime')} *`}
                    type="time"
                    value={arrivalTime}
                    onChange={(e) => setArrivalTime(e.target.value)}
                    className={`${FIELD} tabular-nums`}
                  />
                </div>
                <p className="text-[12px] text-slate-500">{t('trip.arrivalHint')}</p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
